import { useState, useEffect } from "react";
import {
    TextField,
    useRecordContext,
    useDataProvider,
    useTranslate,
    usePermissions,
    useNotify,
    useRefresh,
    useResourceContext,
    ListContextProvider,
    useList,
} from "react-admin";
import { MenuItem, Menu, Button, Box, CircularProgress } from "@mui/material";
import { handleHttpError } from "shared/src/utils/errorHandler";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import { RaDateField } from "shared/src/components/fields/Ra/RaDateField";
import { MediaDialog } from "./MediaDialog";
import { DatagridWithHistory } from "shared/src/components/DatagridWithHistory";
import { useInfiniteScroll } from "shared/src/hooks/useInfiniteScroll";

export const MediaManagement = (props) => {
    const resource = useResourceContext();
    const { resourceFilterKey } = props;
    const record = useRecordContext();
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const { isLoading, permissions } = usePermissions();
    const refresh = useRefresh();

    const [mediaTools, setMediaTools] = useState({
        edit: null,
        type: null,
        id: null,
        record: null,
    });

    const {
        data: mediaData,
        isPending,
        isFetchingNextPage,
        observerRef,
    } = useInfiniteScroll(
        "media",
        {
            filter: {
                [resourceFilterKey]: record?.id
            },
            pagination: { page: 1, perPage: 25 },
            sort: { field: "id", order: "DESC" },
        },
        { enabled: !!record?.id }
    );

    const listContext = useList({ data: mediaData, resource: "media" });

    const openRecord = (id, _, mediaRecord) => {
        const mediaToolsOptions = {
            edit: false,
            type: mediaRecord.type,
            id: id,
            label: "equip.showMedia",
        };
        setMediaTools(mediaToolsOptions);

        dataProvider
            .getOne("media", { id: id })
            .then((response) => {
                setMediaTools((current) =>
                    current && current.id === id
                        ? { ...current, record: response.data }
                        : current
                );
            })
            .catch((e) => {
                handleHttpError(e, notify);
            });

        return false; // Prevent default redirect
    };

    useEffect(() => {
        if (!mediaTools.type) {
            refresh();
        }
    }, [mediaTools.type, refresh]);

    if (isLoading) {
        return <></>;
    }

    if (isPending) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            <ListContextProvider value={listContext}>
                <MediaDatagridWithToolbar
                    {...props}
                    openRecord={openRecord}
                    setMediaTools={setMediaTools}
                    permissions={permissions}
                    mediaTools={mediaTools}
                    resource={resource}
                    mediaData={mediaData}
                />
            </ListContextProvider>
            <Box ref={observerRef} sx={{ display: "flex", justifyContent: "center", p: 2 }}>
                {isFetchingNextPage && <CircularProgress size={24} />}
            </Box>
        </Box>
    );
};

const MediaDatagridWithToolbar = ({
    openRecord,
    setMediaTools,
    permissions,
    mediaTools,
    resource,
    mediaData,
    ...props
}) => {
    const mediaImages =
        mediaData?.filter((m) => m.type === "file" && m.mimetype?.startsWith("image/")) || [];

    return (
        <>
            {mediaTools.type ? (
                <MediaDialog
                    {...props}
                    mediaTools={mediaTools}
                    setMediaTools={setMediaTools}
                    mediaImages={mediaImages}
                    resource={resource}
                />
            ) : null}
            {permissions.vote({
                resource: "media",
                attribute: "create",
                subject: null,
            }) && <Toolbar setMediaTools={setMediaTools} />}
            <DatagridWithHistory redirect={false} rowClick={openRecord} bulkActionButtons={false}>
                <TextField label="equip.type" source="type" />
                <TextField label="equip.name" source="shortname" />
                <RaDateField label="equip.created" source="createdAt" />
                <TextField label="equip.size" source="size" />
            </DatagridWithHistory>
        </>
    );
};

const Toolbar = ({ setMediaTools }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);
    const translate = useTranslate();
    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };
    const selectMenuOption = (e) => {
        setMediaTools({
            edit: true,
            type: e.target.dataset.menuitem,
            label: e.target.dataset.label,
        });
        handleClose();
    };

    return (
        <Box
            sx={{
                px: 1,
                py: 2,
                display: "flex",
                gap: 1
            }}>
            <Button
                id="add-media-button"
                aria-controls={open ? "add-media-menu" : undefined}
                aria-haspopup="true"
                aria-expanded={open ? "true" : undefined}
                variant="text"
                onClick={handleClick}
                startIcon={<AddPhotoAlternateIcon fontSize="large" />}
                size="small"
                title={translate("equip.addMedia")}
            >
                {translate("equip.addMedia")}
            </Button>
            <Menu
                id="add-media-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: "top",
                    horizontal: "left",
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "left",
                }}
                slotProps={{
                    list: {
                        "aria-labelledby": "add-media-button",
                    }
                }}
            >
                <MenuItem
                    title={translate("equip.uploadMedia")}
                    aria-label={translate("equip.uploadMedia")}
                    data-menuitem={"file"}
                    data-label={translate("equip.uploadMedia")}
                    onClick={selectMenuOption}
                >
                    {translate("equip.uploadMedia")}
                </MenuItem>
                <MenuItem
                    title={translate("equip.linkToExternalResource")}
                    aria-label={translate("equip.linkToExternalResource")}
                    data-menuitem={"link"}
                    data-label={translate("equip.linkToExternalResource")}
                    onClick={selectMenuOption}
                >
                    {translate("equip.linkToExternalResource")}
                </MenuItem>
                <MenuItem
                    title={translate("equip.addText")}
                    aria-label={translate("equip.addText")}
                    data-menuitem={"text"}
                    data-label={translate("equip.addText")}
                    onClick={selectMenuOption}
                >
                    {translate("equip.addText")}
                </MenuItem>
            </Menu>
        </Box>
    );
};
